import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { onAnimationEnd } from '@theme/utilities';

/**
 * Bayse cart drawer with div-based overlay and panel.
 *
 * @typedef {object} Refs
 * @property {HTMLDivElement} overlay - Full-screen overlay wrapper.
 * @property {HTMLDivElement} backdrop - Clickable backdrop.
 * @property {HTMLDivElement} panel - Drawer panel.
 * @property {HTMLButtonElement} closeButton - Close button.
 *
 * @extends Component<Refs>
 */
class BayseCartDrawer extends Component {
  #previousScrollY = 0;
  /** @type {HTMLElement | null} */
  #lastTrigger = null;

  connectedCallback() {
    super.connectedCallback();
    requestIdleCallback(() => {
      this.#bindEvents();
      this.#bindCartTriggers();
    });
  }

  disconnectedCallback() {
    this.#unbindEvents();
    document.removeEventListener('click', this.#handleCartTriggerClick);
    document.removeEventListener('keydown', this.#handleKeyDown);
    super.disconnectedCallback();
  }

  #bindEvents() {
    this.refs.closeButton?.addEventListener('click', this.closeModal);
    this.refs.backdrop?.addEventListener('click', this.#handleBackdropClick);
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
  }

  #unbindEvents() {
    this.refs.closeButton?.removeEventListener('click', this.closeModal);
    this.refs.backdrop?.removeEventListener('click', this.#handleBackdropClick);
    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
  }

  /**
   * Opens the cart drawer when an item is added to cart.
   * @param {CustomEvent} event
   */
  #handleCartUpdate = (event) => {
    console.log('handleCartUpdate', event);
    if (event.detail?.data?.didError) return;
    this.openModal();
  };

  /**
   * Opens the drawer from header cart triggers.
   */
  #bindCartTriggers() {
    document.addEventListener('click', this.#handleCartTriggerClick);
  }

  /**
   * Routes cart icon clicks to the Bayse cart drawer.
   *
   * @param {Event} event - Click event.
   */
  #handleCartTriggerClick = (event) => {
    const trigger = event.target instanceof Element
      ? event.target.closest('[data-bayse-cart-open]')
      : null;

    if (!trigger) return;

    event.preventDefault();
    this.openModal(event);
  };

  /**
   * Opens the cart drawer.
   *
   * @param {Event} [event] - Optional trigger event.
   */
  openModal = (event) => {
    const { overlay } = this.refs;

    if (!overlay || overlay.hasAttribute('data-open')) return;

    if (event?.target instanceof HTMLElement) {
      this.#lastTrigger = event.target.closest('button, a') ?? event.target;
    }

    this.#previousScrollY = window.scrollY;
    overlay.removeAttribute('data-closing');
    overlay.setAttribute('data-open', '');
    document.documentElement.setAttribute('scroll-lock', '');
    document.addEventListener('keydown', this.#handleKeyDown);
  };

  /**
   * Closes the cart drawer.
   */
  closeModal = async () => {
    const { overlay, panel } = this.refs;

    if (!overlay?.hasAttribute('data-open') || overlay.hasAttribute('data-closing')) return;

    overlay.setAttribute('data-closing', '');

    if (panel) {
      await new Promise((resolve) => requestAnimationFrame(resolve));
      await onAnimationEnd(panel, undefined, { subtree: false });
    }

    overlay.removeAttribute('data-open');
    overlay.removeAttribute('data-closing');
    document.documentElement.removeAttribute('scroll-lock');
    document.removeEventListener('keydown', this.#handleKeyDown);
    window.scrollTo({ top: this.#previousScrollY, behavior: 'instant' });
    this.#lastTrigger?.focus();
    this.#lastTrigger = null;
  };

  /**
   * Closes the modal when the backdrop is clicked.
   *
   * @param {MouseEvent} event - Click event.
   */
  #handleBackdropClick = (event) => {
    if (event.target !== this.refs.backdrop) return;
    this.closeModal();
  };

  /**
   * Closes the modal on Escape key press.
   *
   * @param {KeyboardEvent} event - Keyboard event.
   */
  #handleKeyDown = (event) => {
    const { overlay } = this.refs;

    if (event.key !== 'Escape' || !overlay?.hasAttribute('data-open')) return;

    event.preventDefault();
    this.closeModal();
  };
}

if (!customElements.get('bayse-cart-drawer')) {
  customElements.define('bayse-cart-drawer', BayseCartDrawer);
}
